import { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getMoonPhaseImage } from '../../utils/hebrewCalendar';
import { PRIMARY_COLOR } from '../../constants/colors';

// Enable debug mode by setting this to true or via localStorage
// If localStorage is 'false', it overrides development mode
const getDebugMoonDrag = () => {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem('DEBUG_MOON_DRAG');
  if (stored === 'false') return false;
  if (stored === 'true') return true;
  return process.env.NODE_ENV === 'development';
};
export default function DraggableMoon({ 
  initialHebrewDay, 
  daysInMonth, 
  size = '80vw', 
  maxSize = '480px',
  className = '',
  style = {},
  onDayChange = null, // Callback: (dayOffset) => void
  currentDayOffset = 0, // External day offset to persist position
  currentHebrewDay = null // Current Hebrew day (for resetting to moonphase-01 on day 1)
}) {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [fractionalDayOffset, setFractionalDayOffset] = useState(currentDayOffset); // State for smooth updates
  const moonRef = useRef(null);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const startDayOffsetRef = useRef(currentDayOffset); // Day offset when drag started
  const currentDayRef = useRef(initialHebrewDay);
  const dragSensitivityRef = useRef(100); // Use ref so it's always current
  const isDraggingRef = useRef(false);
  const isHorizontalDragRef = useRef(false); // Track if this is a horizontal drag
  const accumulatedDayOffsetRef = useRef(currentDayOffset); // Track total day offset
  const lastWholeDayOffsetRef = useRef(Math.round(currentDayOffset)); // Track last whole day for onDayChange
  const isDraggingStateRef = useRef(false); // Track dragging state for useEffect checks
  const lastTouchMoveTimeRef = useRef(0); // Track when last touch move occurred
  const lastDragEndTimeRef = useRef(0); // Track when drag ended to prevent premature updates
  const isInDragOperationRef = useRef(false); // Track if we're actively in a drag operation (from touchStart to touchEnd)

  const MAX_DAYS_OFFSET = 15;

  // Calculate drag sensitivity based on moon size (original implementation)
  // Desktop: Full animation (15 days) in one moon width
  // Mobile: Full animation (15 days) by edge of screen
  useEffect(() => {
    const calculateSensitivity = () => {
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // Mobile: Full screen width (100vw) = 8 days
        // This gives fine-grained control: small drags = small day changes
        dragSensitivityRef.current = window.innerWidth / 8;
      } else {
        // Desktop: calculate based on moon width
        if (moonRef.current) {
          const moonWidth = moonRef.current.offsetWidth;
          if (moonWidth > 0) {
            // For 30 days (one full cycle), use 3x moon width
            dragSensitivityRef.current = (moonWidth * 3) / 30;
          }
        }
      }
    };

    // Calculate on mount and when size changes
    calculateSensitivity();

    // Recalculate on window resize (important for mobile orientation changes)
    window.addEventListener('resize', calculateSensitivity);
    return () => window.removeEventListener('resize', calculateSensitivity);
  }, [size, maxSize]);

  useEffect(() => {
    currentDayRef.current = initialHebrewDay;
    // Only update from external offset when NOT dragging
    // During drag, we manage fractionalDayOffset ourselves
    // Check multiple indicators to ensure we don't reset during active drag:
    // 1. isDraggingStateRef - ref that tracks drag state
    // 2. isDragging - React state for drag
    // 3. isDraggingRef - ref that tracks if we're actively in a drag gesture
    // 4. recentTouchMove - if we've had touch activity recently (within 1000ms - increased for reliability)
    // 5. recentDragEnd - if drag ended recently (within 300ms - increased for reliability)
    // 6. isInDragOperationRef - PRIMARY: if we're actively in a drag operation (touchStart to touchEnd)
    //    This is the most reliable indicator since it's set synchronously in touchStart
    const now = Date.now();
    const recentTouchMove = now - lastTouchMoveTimeRef.current < 1000; // Increased from 500ms
    const recentDragEnd = now - lastDragEndTimeRef.current < 300; // Increased from 200ms
    // isInDragOperationRef is checked FIRST as it's the most reliable synchronous indicator
    const isCurrentlyDragging = isInDragOperationRef.current || isDraggingStateRef.current || isDragging || isDraggingRef.current || recentTouchMove || recentDragEnd;
    
    // Also check if the change is small (less than 1 day difference) - this suggests
    // it's a drag update, not an external jump (holiday, "Return to Today", etc.)
    const offsetDifference = Math.abs(currentDayOffset - accumulatedDayOffsetRef.current);
    const isSmallChange = offsetDifference < 1.5; // Allow some tolerance for rounding
    
    // DEFENSIVE CHECK: Is fractionalDayOffset being actively managed by drag?
    // If fractionalDayOffset differs significantly from currentDayOffset, it means
    // the drag is actively managing it (smooth transitions between whole days)
    // We should NOT overwrite it in that case, even if drag indicators are false
    const fractionalDifference = Math.abs(fractionalDayOffset - currentDayOffset);
    const isFractionalBeingManaged = fractionalDifference > 0.5; // If more than 0.5 days different, it's being managed
    
    // Only update if:
    // 1. We're not currently dragging (all indicators are false)
    // 2. AND fractionalDayOffset is NOT being actively managed by drag
    // 3. AND either the change is large (external jump) OR the accumulated offset matches current
    const currentFractional = fractionalDayOffset;
    const shouldUpdate = !isCurrentlyDragging && 
                        !isFractionalBeingManaged && 
                        (!isSmallChange || Math.abs(currentFractional - accumulatedDayOffsetRef.current) < 0.1);
    
    if (shouldUpdate) {
      if (getDebugMoonDrag() && currentDayOffset !== accumulatedDayOffsetRef.current) {
        console.log('[DraggableMoon] useEffect: Updating from external offset', {
          currentDayOffset,
          was: accumulatedDayOffsetRef.current,
          fractionalDayOffset,
          fractionalDifference,
          isFractionalBeingManaged,
          isDraggingStateRef: isDraggingStateRef.current,
          isDraggingState: isDragging,
          isDraggingRef: isDraggingRef.current,
          isInDragOperationRef: isInDragOperationRef.current,
          recentTouchMove,
          recentDragEnd,
          offsetDifference,
          isSmallChange,
          isCurrentlyDragging: isCurrentlyDragging
        });
      }
      accumulatedDayOffsetRef.current = currentDayOffset;
      setFractionalDayOffset(currentDayOffset);
      // Reset drag offset when external offset changes (e.g., "Return to Today" button)
      setDragOffset(0);
    } else {
      // During drag or when fractionalDayOffset is being managed, still update accumulatedDayOffsetRef 
      // to track the base, but don't override fractionalDayOffset which is being calculated from drag
      if (getDebugMoonDrag() && (isCurrentlyDragging || isFractionalBeingManaged)) {
        console.log('[DraggableMoon] useEffect: Skipping update', {
          reason: isCurrentlyDragging ? 'during drag' : 'fractional being managed',
          currentDayOffset,
          fractionalDayOffset,
          fractionalDifference,
          isFractionalBeingManaged,
          isDraggingStateRef: isDraggingStateRef.current,
          isDraggingState: isDragging,
          isDraggingRef: isDraggingRef.current,
          isInDragOperationRef: isInDragOperationRef.current,
          recentTouchMove,
          recentDragEnd,
          isCurrentlyDragging: isCurrentlyDragging
        });
      }
      // Always update accumulatedDayOffsetRef to track the latest value
      accumulatedDayOffsetRef.current = currentDayOffset;
    }
  }, [initialHebrewDay, currentDayOffset]);

  // Preload all moon images for the animation range (-15 to +15 days)
  useEffect(() => {
    const imagesToPreload = [];
    
    // Generate all days in the range -15 to +15
    for (let offset = -MAX_DAYS_OFFSET; offset <= MAX_DAYS_OFFSET; offset++) {
      const targetDay = initialHebrewDay + offset;
      let adjustedDay = targetDay;
      let adjustedDaysInMonth = daysInMonth;

      // Handle month boundaries - same logic as getCurrentMoonImage
      if (targetDay < 1) {
        // Previous month - use last day of previous month
        adjustedDay = Math.max(1, daysInMonth + targetDay);
        adjustedDaysInMonth = 30; // Default for previous month
      } else if (targetDay > daysInMonth) {
        // Next month - wrap to beginning
        adjustedDay = targetDay - daysInMonth;
        adjustedDaysInMonth = 30; // Default for next month
      }

      // Get the image path for this day
      const imagePath = getMoonPhaseImage(adjustedDay, adjustedDaysInMonth);
      
      // Preload the image
      const img = new Image();
      img.src = imagePath;
      imagesToPreload.push(img);
    }

    // Preload images silently
    Promise.all(imagesToPreload.map(img => {
      return new Promise((resolve, reject) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = resolve;
          img.onerror = reject;
        }
      });
    })).catch(() => {
      // Silently handle preload errors
    });
  }, [initialHebrewDay, daysInMonth]);

  // Add touch event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const moonElement = moonRef.current;
    if (!moonElement) return;

    const touchStart = (e) => {
      // Don't prevent default yet - wait to see if it's horizontal movement
      startXRef.current = e.touches[0].clientX;
      startYRef.current = e.touches[0].clientY;
      isDraggingRef.current = false;
      isHorizontalDragRef.current = false;
      isDraggingStateRef.current = false; // Reset drag state at start
      isInDragOperationRef.current = true; // Mark that we're in a drag operation
      // Use the current fractional offset as the starting point for the drag
      startDayOffsetRef.current = fractionalDayOffset;
      setDragOffset(0);
      // Update last touch time to prevent premature reset
      lastTouchMoveTimeRef.current = Date.now();
    };

    const touchMove = (e) => {
      if (!e.touches || e.touches.length === 0) {
        if (getDebugMoonDrag()) console.log('[DraggableMoon] touchMove: No touches!');
        return;
      }
      
      const deltaX = e.touches[0].clientX - startXRef.current;
      const deltaY = e.touches[0].clientY - startYRef.current;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      
      // Determine if this is primarily horizontal movement
      const isHorizontal = absDeltaX > absDeltaY;
      
      // If we haven't started dragging yet, check if it's horizontal
      if (!isDraggingRef.current) {
        if (isHorizontal && absDeltaX > 5) {
          // Start horizontal drag - now prevent default
          // CRITICAL: Set ALL drag indicators BEFORE proceeding
          if (getDebugMoonDrag()) console.log('[DraggableMoon] Starting drag');
          isDraggingRef.current = true;
          isHorizontalDragRef.current = true;
          isDraggingStateRef.current = true;
          setIsDragging(true);
          isInDragOperationRef.current = true; // Ensure this is set when drag starts
          lastTouchMoveTimeRef.current = Date.now(); // Set touch time immediately
          e.preventDefault();
        } else if (!isHorizontal && absDeltaY > 5) {
          // This is vertical scrolling - don't prevent default, allow page scroll
          return;
        } else {
          // Not enough movement yet, wait
          return;
        }
      }
      
      // Only handle if we're in a horizontal drag
      if (isDraggingRef.current && isHorizontalDragRef.current) {
        e.preventDefault();
        
        // CRITICAL: Set ALL drag indicators BEFORE any state updates or callbacks
        // This ensures useEffect will see them as true when it runs after onDayChange
        isDraggingStateRef.current = true;
        setIsDragging(true);
        isInDragOperationRef.current = true; // PRIMARY indicator - must be true during entire drag
        lastTouchMoveTimeRef.current = Date.now(); // Update timestamp BEFORE calling onDayChange
        
        // Invert direction: Right = backward, Left = forward
        // Mobile: 100vw = 8 days, so dragSensitivity = window.innerWidth / 8
        const dayDelta = -deltaX / dragSensitivityRef.current;
        const newDayOffset = startDayOffsetRef.current + dayDelta;
        
        setDragOffset(dayDelta * dragSensitivityRef.current);
        
        // Update with fractional precision for smoother transitions
        // Round to 0.1 day precision for smooth image updates
        const roundedOffset = Math.round(newDayOffset * 10) / 10;
        
        // Update state to trigger re-render and smooth image transitions
        setFractionalDayOffset(roundedOffset);
        accumulatedDayOffsetRef.current = roundedOffset;
        
        // Call onDayChange AFTER all indicators are set
        // This ensures useEffect will see isInDragOperationRef.current === true
        // Round to whole days for the parent component
        const wholeDayOffset = Math.round(roundedOffset);
        if (onDayChange) {
          // Only call if the whole day actually changed to reduce unnecessary updates
          if (wholeDayOffset !== lastWholeDayOffsetRef.current) {
            if (getDebugMoonDrag()) {
              console.log('[DraggableMoon] onDayChange:', {
                wholeDayOffset,
                previous: lastWholeDayOffsetRef.current,
                fractional: roundedOffset,
                deltaX,
                dayDelta
              });
            }
            lastWholeDayOffsetRef.current = wholeDayOffset;
            onDayChange(wholeDayOffset);
          }
        }
      }
    };

    const touchEnd = (e) => {
      if (getDebugMoonDrag()) console.log('[DraggableMoon] touchEnd', {
        wasDragging: isDraggingRef.current,
        wasHorizontal: isHorizontalDragRef.current
      });
      
      if (isDraggingRef.current && isHorizontalDragRef.current) {
        e.preventDefault();
        
        const deltaX = e.changedTouches[0]?.clientX - startXRef.current || 0;
        const dayDelta = -deltaX / dragSensitivityRef.current;
        const finalDayOffset = startDayOffsetRef.current + dayDelta;
        const finalRoundedOffset = Math.round(finalDayOffset * 10) / 10;
        const finalWholeDayOffset = Math.round(finalDayOffset);
        
        accumulatedDayOffsetRef.current = finalRoundedOffset;
        setFractionalDayOffset(finalRoundedOffset);
        setDragOffset(0);
        
        // Update last whole day offset
        lastWholeDayOffsetRef.current = finalWholeDayOffset;
        
        if (onDayChange) {
          onDayChange(finalWholeDayOffset);
        }
      }
      
      // Delay resetting drag state to prevent race conditions with useEffect
      // This gives time for any pending state updates to complete
      setTimeout(() => {
        if (getDebugMoonDrag()) {
          console.log('[DraggableMoon] touchEnd: Resetting drag state');
        }
        isDraggingRef.current = false;
        isHorizontalDragRef.current = false;
        isDraggingStateRef.current = false;
        setIsDragging(false);
        // Record when drag ended to prevent premature updates
        lastDragEndTimeRef.current = Date.now();
        // Mark that drag operation is complete
        isInDragOperationRef.current = false;
        // Clear touch time after a delay to allow useEffect to process final state
        setTimeout(() => {
          lastTouchMoveTimeRef.current = 0;
        }, 100);
      }, 50);
    };
    
    const touchCancel = (e) => {
      if (getDebugMoonDrag()) console.log('[DraggableMoon] touchCancel');
      isInDragOperationRef.current = false; // Mark that drag operation is cancelled
      touchEnd(e);
    };

    // Add listeners with passive: false
    moonElement.addEventListener('touchstart', touchStart, { passive: false });
    moonElement.addEventListener('touchmove', touchMove, { passive: false });
    moonElement.addEventListener('touchend', touchEnd, { passive: false });
    moonElement.addEventListener('touchcancel', touchCancel, { passive: false });

    return () => {
      moonElement.removeEventListener('touchstart', touchStart);
      moonElement.removeEventListener('touchmove', touchMove);
      moonElement.removeEventListener('touchend', touchEnd);
      moonElement.removeEventListener('touchcancel', touchEnd);
    };
  }, [onDayChange]);

  // Get current moon image based on drag offset with smooth wrapping
  // Use fractional offsets for smoother transitions during drag
  const getCurrentMoonImage = () => {
    // Use fractional offset state for smooth transitions during drag
    // When dragging, use fractionalDayOffset (which updates smoothly)
    // When not dragging, use currentDayOffset directly to ensure immediate updates
    const effectiveOffset = isDragging 
      ? fractionalDayOffset
      : (currentDayOffset !== undefined ? currentDayOffset : fractionalDayOffset);
    
    // Calculate the actual day offset - allow unlimited offset, wrap for image selection
    // Wrap to range -15 to +15 for image selection, but allow unlimited scrolling
    // Use modulo 30 to wrap smoothly (30 days in a lunar month)
    const wrappedOffset = ((effectiveOffset % 30) + 30) % 30;
    const dayInCycle = wrappedOffset <= 15 ? wrappedOffset : wrappedOffset - 30;
    // Round to nearest day for image selection (but use fractional for smooth transitions)
    const roundedOffset = Math.round(dayInCycle);
    
    // Calculate target day with proper month wrapping
    const targetDay = initialHebrewDay + roundedOffset;
    let adjustedDay = targetDay;
    let adjustedDaysInMonth = daysInMonth;

    // Handle month boundaries - wrap around smoothly
    if (targetDay < 1) {
      // Previous month - use last day of previous month
      adjustedDay = Math.max(1, daysInMonth + targetDay);
      adjustedDaysInMonth = 30; // Default for previous month
    } else if (targetDay > daysInMonth) {
      // Next month - wrap to beginning
      adjustedDay = targetDay - daysInMonth;
      adjustedDaysInMonth = 30; // Default for next month
    }

    // Reset to moonphase-01 on the first of each Hebrew month
    // Use currentHebrewDay if provided (more accurate), otherwise use adjustedDay
    const actualDay = currentHebrewDay !== null ? currentHebrewDay : adjustedDay;
    if (actualDay === 1) {
      return getMoonPhaseImage(1, adjustedDaysInMonth);
    }

    return getMoonPhaseImage(actualDay, adjustedDaysInMonth);
  };
  
  // Calculate moon image - use useMemo with fractionalDayOffset for smooth updates
  const moonImage = useMemo(() => {
    return getCurrentMoonImage();
  }, [isDragging, fractionalDayOffset, currentDayOffset, initialHebrewDay, daysInMonth, currentHebrewDay]);

  const handleStart = (clientX, clientY) => {
    // Only start if we have valid coordinates
    if (clientX === undefined || clientY === undefined) return;
    
    startXRef.current = clientX;
    startYRef.current = clientY || 0;
    isDraggingRef.current = false;
    isHorizontalDragRef.current = false;
    startDayOffsetRef.current = accumulatedDayOffsetRef.current;
    setDragOffset(0); // Reset visual offset, we'll calculate from deltaX
  };

  const handleMove = (clientX, clientY) => {
    // Only process movement if we've started a drag (pointer was down)
    // Check if we have a valid start position (indicates pointer down occurred)
    if (startXRef.current === null && startYRef.current === null && !isDraggingRef.current) {
      return; // No pointer down event, ignore movement
    }
    
    const deltaX = clientX - startXRef.current;
    const deltaY = (clientY || 0) - startYRef.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Determine if this is primarily horizontal movement
    const isHorizontal = absDeltaX > absDeltaY;
    
    // If we haven't started dragging yet, check if it's horizontal
    if (!isDraggingRef.current) {
      if (isHorizontal && absDeltaX > 5) {
        // Start horizontal drag
        isDraggingRef.current = true;
        isHorizontalDragRef.current = true;
        isDraggingStateRef.current = true;
        setIsDragging(true);
      } else if (!isHorizontal && absDeltaY > 5) {
        // This is vertical scrolling - don't handle, allow page scroll
        return;
      } else {
        // Not enough movement yet, wait
        return;
      }
    }
    
    // Only handle if we're in a horizontal drag
    if (isDraggingRef.current && isHorizontalDragRef.current) {
      // Convert pixel movement to day offset
      // Invert direction: Right (positive deltaX) = backward in time (negative day offset)
      // Left (negative deltaX) = forward in time (positive day offset)
      const dayDelta = -deltaX / dragSensitivityRef.current;
      const newDayOffset = startDayOffsetRef.current + dayDelta;
      
      // Update visual offset for smooth animation
      setDragOffset(dayDelta * dragSensitivityRef.current);
      
      // Notify parent of the new day offset (allow unlimited)
      const roundedOffset = Math.round(newDayOffset);
      if (onDayChange && roundedOffset !== accumulatedDayOffsetRef.current) {
        accumulatedDayOffsetRef.current = roundedOffset;
        onDayChange(roundedOffset);
      }
    }
  };

  const handleEnd = () => {
    if (isDraggingRef.current && isHorizontalDragRef.current) {
      // Final offset is already calculated in handleMove, just persist it
      setDragOffset(0);
    }
    
    isDraggingRef.current = false;
    isHorizontalDragRef.current = false;
    isDraggingStateRef.current = false;
    setIsDragging(false);
    // Reset start positions to indicate no active drag
    startXRef.current = null;
    startYRef.current = null;
  };

  // Touch handlers are now handled via useEffect with passive: false

  // Pointer handlers for desktop
  const handlePointerDown = (e) => {
    // Don't prevent default yet - wait to see if it's horizontal movement
    handleStart(e.clientX, e.clientY);
    moonRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    // Only process if we have pointer capture (pointer was down on this element)
    if (e.pointerId && !moonRef.current?.hasPointerCapture(e.pointerId)) return;
    
    // Only prevent default if we're in a horizontal drag
    if (isDraggingRef.current && isHorizontalDragRef.current) {
      e.preventDefault();
    }
    
    // Only call handleMove if we have pointer capture or are already dragging
    if (e.pointerId && moonRef.current?.hasPointerCapture(e.pointerId)) {
      handleMove(e.clientX, e.clientY);
    } else if (isDraggingRef.current) {
      handleMove(e.clientX, e.clientY);
    }
  };

  const handlePointerUp = (e) => {
    if (e.pointerId) {
      moonRef.current?.releasePointerCapture(e.pointerId);
    }
    
    if (isDraggingRef.current && isHorizontalDragRef.current) {
      e.preventDefault();
    }
    
    handleEnd();
  };

  // moonImage is now calculated in useMemo above
  const effectiveOffset = isDragging 
    ? (startDayOffsetRef.current + dragOffset / dragSensitivityRef.current)
    : accumulatedDayOffsetRef.current;
  const roundedOffset = Math.round(effectiveOffset);

  return (
    <div 
      className={`relative ${className}`}
      style={{
        ...style,
        pointerEvents: 'auto' // Re-enable pointer events on the moon itself
      }}
    >
      {/* Debug overlay - only visible when getDebugMoonDrag() is true */}
      {getDebugMoonDrag() && (
        <div 
          className="absolute top-0 left-0 bg-black/80 text-white text-xs p-2 rounded z-50 pointer-events-none"
          style={{ fontFamily: 'monospace' }}
        >
          <div>Fractional: {fractionalDayOffset.toFixed(2)}</div>
          <div>Whole: {Math.round(fractionalDayOffset)}</div>
          <div>Dragging: {isDragging ? 'Yes' : 'No'}</div>
          <div>InDragOp: {isInDragOperationRef.current ? 'Yes' : 'No'}</div>
          <div>Last Sent: {lastWholeDayOffsetRef.current}</div>
        </div>
      )}
      {/* Main moon - draggable */}
      <motion.div
        ref={moonRef}
        className="relative cursor-grab active:cursor-grabbing select-none"
        style={{
          width: size,
          height: size,
          maxWidth: maxSize,
          maxHeight: maxSize,
          touchAction: 'pan-y', // Allow vertical panning (scrolling)
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        animate={{
          scale: 1, // Moon never changes size
        }}
        transition={{
          scale: { duration: 0.1 }
        }}
      >
        <img 
          src={moonImage} 
          alt="Moon phase" 
          className="object-contain w-full h-full pointer-events-none"
          draggable={false}
        />
        {/* Multiply filter overlay */}
        <div 
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{ 
            backgroundColor: PRIMARY_COLOR,
            mixBlendMode: 'multiply',
            opacity: 0.5
          }}
        />
      </motion.div>
    </div>
  );
}

